import React, { useMemo } from 'react';

import { Radio } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Participant from 'components/participants/Participant';
import { BodyRegular, TableHeading } from 'components/typography/Typography';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import { type SeedAssignment } from 'typedefs/store/tmxTypes';

export const getSeed = (participantId: string, seedAssignments: SeedAssignment[]) => {
  const seedAssignment = seedAssignments?.find((assignment) => assignment.participantId === participantId);
  if (seedAssignment) return seedAssignment.seedValue ?? seedAssignment.seedNumber;
  return '';
};

interface ColumnProps {
  selectedRow: string;
  onChangeSelected: (participantId: string) => void;
  isTeam?: boolean;
  isDoubles?: boolean;
  isModifyDoublesPair?: boolean;
  seedAssignments?: SeedAssignment[];
}

export const useColumns = ({
  selectedRow,
  onChangeSelected,
  isTeam,
  isDoubles,
  isModifyDoublesPair,
  seedAssignments,
}: ColumnProps) => {
  const { t } = useTranslation();
  const showPlayerProfile = usePlayersProfileDialog();

  return useMemo(
    () => [
      {
        key: 'select',
        getTitle: () => ({ node: '' }),
        getValue: (row) => {
          return {
            node: (
              <Radio
                checked={selectedRow === row?.participant?.participantId}
                color="primary"
                value={row?.participant?.participantId}
                onChange={(e) => onChangeSelected(e.target.value)}
              />
            ),
          };
        },
      },
      ...(isModifyDoublesPair
        ? [
            {
              key: 'order',
              getTitle: () => ({
                node: <TableHeading>{t('order')}</TableHeading>,
              }),
              getValue: (row) => ({ node: <BodyRegular>{row?.index}</BodyRegular> }),
            },
          ]
        : []),
      {
        key: 'player',
        getTitle: () => ({
          node: <TableHeading>{isTeam ? t('team') : t('player')}</TableHeading>,
        }),
        getValue: (row) => {
          return {
            node: (
              <Participant
                showAdditionalInfo
                isDoubles={isDoubles}
                participantId={row?.participant?.participantId}
                onClick={() =>
                  showPlayerProfile([
                    {
                      participantId: row?.participant?.participantId,
                      participantName: row?.participant?.participantName,
                    },
                  ])
                }
              />
            ),
          };
        },
      },
      ...(!isModifyDoublesPair
        ? [
            {
              key: 'seed',
              getTitle: () => ({
                node: <TableHeading>{t('seed')}</TableHeading>,
              }),
              getValue: (row) => ({
                node: <BodyRegular>{getSeed(row?.participant?.participantId, seedAssignments)}</BodyRegular>,
              }),
            },
            {
              key: 'type',
              getTitle: () => ({
                node: <TableHeading>{t('type')}</TableHeading>,
              }),
              getValue: (row) => ({ node: <BodyRegular>{row.type}</BodyRegular> }),
            },
          ]
        : []),
    ],
    [isModifyDoublesPair, selectedRow, onChangeSelected, t, isTeam, isDoubles, showPlayerProfile, seedAssignments],
  );
};
